import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Close, Filter } from '@travel/icons/ui';
import {
  useDeviceType,
  useDialogHandler,
  useGetTranslation,
  useOnResizeDebounce,
  MOBILE_SCREEN,
} from '@travel/traveler-core/hooks';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { FlatButton } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import ButtonNoStyle from 'components/ButtonNoStyle';
import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';
import IconButtonWithBadge from 'components/IconButtonWithBadge';
import ProviderFilterGroup from 'components/ProviderFilterGroup';
import ProviderFilterPrice from 'components/ProviderFilterPrice';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import useOutsideClick from 'hooks/useOutsideClick';
import { getFilters as getFiltersPlanList } from 'store/providerPlanList/selectors';
import { getFilters as getFiltersRoomList } from 'store/providerRatePlan/selectors';
import { PlanFilter } from '../RoomList';

import { FILTER_GROUP_IDS, PROVIDER_INFO_SORT_OPTIONS, SortKeysType } from 'constants/provider';
import { Translate } from 'core/translate';
import { ProviderInfoListTypes } from 'ProviderInformation-Types';

import styles from './searchForm.module.scss';

type Props = {
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  /** Where the search bar is on */
  listType: ProviderInfoListTypes;
  /** Callback function which is called when search button is clicked */
  onClickSearch?: () => void;
  /** total of filtered provider plans */
  total?: number;
  /** Callback function which is called when slider animation needed */
  onPriceSliderUpdate?: (min?: number | null, max?: number | null) => void;
  /** Callback function which is called when any tag filter is clicked */
  onFilterChange?: (options: PlanFilter) => void;
  /** Callback function which is called when clear button has been clicked */
  onClear?: () => void;
  /** Callback function which is called when dialog has been closed with user changes */
  onReset?: () => void;
  /** Number of filters applied */
  filterCount?: number | null;
  filterContentWrapper?: string;
  setFilterButtonWidth?: (width: number) => void;
  /** Callback function which is called when sort change */
  onChangeSortKey?: (filterGroupId: string, toggleOption: string) => void;
  /** sortKey of SortKeyType */
  sortKey?: SortKeysType;
  /** boolean of current selected free cancellation*/
  freeCancellation?: boolean;
  /** Callback function which is called when freeCancellation filter is selected */
  onFreeCancellationUpdate?: () => void;
} & PlanFilter;

function FilterButton(props: Props) {
  const { listType, setFilterButtonWidth } = props;

  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const { isOpen, onClose, onOpen, onToggle } = useDialogHandler(false);
  const filterOptionsRoomList = useSelector(getFiltersRoomList);
  const filterOptionsPlanList = useSelector(getFiltersPlanList);
  const initialOptions =
    listType === PROVIDER_LIST_TYPE.ROOM_LIST ? filterOptionsRoomList : filterOptionsPlanList;

  const { pageType, siteSection, parentName } = RAT_PAGE_TYPE_OBJ[props.onPage];

  const filterButtonRef = useRef(null);
  const filterWindowRef = useRef(null);

  useOutsideClick(filterWindowRef, filterButtonRef, onToggle);

  const onSubmit = () => {
    setTimeout(() => {
      onClose();
      props.onClickSearch?.();
    }, 100);
  };

  const onClickCloseFilter = () => {
    // going to close
    props.onReset?.();
    onClose();
  };

  const updateFilterButtonWidth = useCallback(() => {
    if (filterButtonRef.current && setFilterButtonWidth && window) {
      const filterButton = filterButtonRef.current as HTMLButtonElement | null;
      if (filterButton instanceof HTMLButtonElement) {
        const width = filterButton.getBoundingClientRect().width || 0;
        const computedStyle = window.getComputedStyle(filterButton);
        const marginLeft = parseInt(computedStyle.marginLeft || '0', 10);
        const marginRight = parseInt(computedStyle.marginRight || '0', 10);
        setFilterButtonWidth(width + marginLeft + marginRight);
      }
    }
  }, [filterButtonRef, setFilterButtonWidth]);

  useEffect(() => {
    setTimeout(() => {
      updateFilterButtonWidth();
    }, 0);
  }, [isOpen, updateFilterButtonWidth]);

  useOnResizeDebounce(updateFilterButtonWidth);

  // to map with current filter (not th original one from current search result)
  const mappedFilterOptions = initialOptions.map(opt => {
    return {
      ...opt,
      features: opt.features?.map(feature => ({
        ...feature,
        selected:
          Boolean(props.features?.includes(feature.featureId)) ||
          Boolean(props.features?.includes(feature.managementName || '')),
      })),
    };
  });

  // Price range handler
  const pendingPriceCallbackRef = useRef<NodeJS.Timeout | number>(0);

  const onPriceChange = (min?: number, max?: number) => {
    props.onPriceSliderUpdate?.(min, max);

    clearTimeout(pendingPriceCallbackRef.current as number);
    pendingPriceCallbackRef.current = setTimeout(() => {
      props.onFilterChange?.({ minPrice: min, maxPrice: max });
    }, 300);
  };

  const handleFreeCancellationUpdate = (_groupId: string, _featureId: string) => {
    props.onFreeCancellationUpdate?.();
    props.onFilterChange?.({ freeCancellation: !props.freeCancellation });
  };

  const onFeatureOptionChange = (_groupId: string, featureId: string) => {
    props.onFilterChange?.({ features: [featureId] });
  };

  const getTranslation = useGetTranslation();
  const freeCancellationString = getTranslation({
    id: 'Cancellation_Policy_Common.Provider_List.Free_Cancellation',
  });

  const sortOptions = useMemo(
    () =>
      Object.values(PROVIDER_INFO_SORT_OPTIONS).map(option => ({
        name: getTranslation({ id: option.labelId }),
        value: option.value,
        featureId: option.value,
        selected: option.value === props.sortKey,
        managementName: option.value,
        isObjective: false,
      })),
    [props.sortKey],
  );

  // filter content
  const filterContent = (
    <>
      {isMobile && (
        <ProviderFilterGroup
          id={FILTER_GROUP_IDS.SORT}
          name={FILTER_GROUP_IDS.SORT}
          title={<Translate id="Provider_List.Search.Sort_Filter.Sort" />}
          options={sortOptions}
          onChange={props.onChangeSortKey}
          type="single"
          className={styles.filterBox}
        />
      )}
      {mappedFilterOptions.map(filter => {
        if (filter.max || filter.min) {
          // price filtering
          return (
            <ProviderFilterPrice
              key="price-range"
              className={cx(styles.filterBox, styles.priceRange)}
              min={filter.min}
              max={filter.max}
              appliedMin={props.minPrice ?? filter?.min}
              appliedMax={props.maxPrice ?? filter?.max}
              onValueUpdate={onPriceChange}
            />
          );
        } else if (filter.freeCancellation) {
          const options = [
            {
              ...filter.freeCancellation,
              featureId: freeCancellationString,
              name: freeCancellationString,
              managementName: freeCancellationString,
              selected: props.freeCancellation,
            },
          ];
          // free cancellation filtering
          return (
            <ProviderFilterGroup
              className={styles.filterBox}
              id={filter.groupId}
              title={
                <Translate id="Cancellation_Policy_Common.Cancellation_Policy_Modal.Section_Title" />
              }
              options={options}
              onChange={handleFreeCancellationUpdate}
              optionTextClassName={styles.optionTextClassName}
            />
          );
        } else {
          // feature filtering
          return (
            isNotEmptyArray(filter.features) && (
              <ProviderFilterGroup
                key={filter.groupId}
                className={styles.filterBox}
                id={filter.groupId}
                title={filter.groupName}
                options={filter.features}
                onChange={onFeatureOptionChange}
                optionTextClassName={styles.filterOptionText}
              />
            )
          );
        }
      })}
    </>
  );

  return (
    <>
      {!isMobile ? (
        <FlatButton
          innerRef={filterButtonRef}
          className={styles.filterButton}
          icon={isOpen ? <Close size={16} /> : <Filter size={16} />}
          classType="secondary"
          onClick={onToggle}
          data-testid="filterButton-filter-button"
        >
          {isOpen ? (
            <Translate
              id="Provider_Info.Provider_Info.Filter.Close_Filters"
              condition={() => 'count > 1'}
            />
          ) : (
            <Translate id="Provider_Info.Filter.Menu" />
          )}
        </FlatButton>
      ) : (
        <IconButtonWithBadge
          number={props.filterCount}
          className={styles.filterButtonIcon}
          icon={<Filter size={16} color="cilantro" />}
          onClick={onOpen}
          data-testid="filterButton-filter-button"
        />
      )}

      {/* Full-screen Dialog */}
      {isOpen && isMobile && (
        <Dialog
          title={<Translate id="Provider_Info.Filter.Filter.Title" />}
          isOpen={isOpen}
          onClose={onClickCloseFilter}
          contentClassName={styles.dialogContentBox}
          buttonLabelPrimary={
            <>
              <Translate id="Provider_Info.Filter.Menu" />
              &nbsp;({props.total || 0})
            </>
          }
          onClickPrimary={onSubmit}
          onClickSecondary={props.onClear}
          buttonLabelSecondary={<Translate id="Provider_Info.Filter.Filter.Navigation.Clear" />}
          isUseContentFlexBox={false}
        >
          {filterContent}
        </Dialog>
      )}

      {/* Popup */}
      {isOpen && !isMobile && (
        <div
          ref={filterWindowRef}
          className={cx(styles.filterContentWrapper, props.filterContentWrapper)}
          data-testid="searchForm-filterButton-filterWrapper"
        >
          <div className={styles.filterHeader}>
            <Translate id="Provider_Info.Filter.Menu" className={styles.filterTitle} />
            <ButtonNoStyle onClick={props.onClear!} className={styles.filterHeaderClearAll}>
              <Translate id="Provider_Info.Filter.Filter.Navigation.Clear" />
            </ButtonNoStyle>
          </div>
          <div className={styles.filterContent}>{filterContent}</div>
          <div className={styles.actionBox}>
            <FlatButton
              onClick={onSubmit}
              className={styles.actionButton}
              data-testid="providerInfo-searchForm-filterButton-submit"
            >
              <Translate id="Provider_Info.Provider_Info.Filter.Search_Button" />
              &nbsp;({props.total || 0})
            </FlatButton>
          </div>
        </div>
      )}
      {isOpen && (
        <DataLayer
          pageName={`${parentName}:filter_rooms_form_popup`}
          pageType={pageType}
          siteSection={siteSection}
        />
      )}
    </>
  );
}

export default FilterButton;
